import { memo, useRef, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { SelectionCheckbox } from '@/components/ui/SelectionCheckbox';
import { TruncatedTooltip } from '@/components/ui/TruncatedTooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { ImagePlaceholder } from '@/components/ui/ImagePlaceholder';
import { UserItem, ItemVisibility } from '@/types/userItem';
import { CONDITION_LABELS, CONDITION_COLORS, CATEGORY_LABELS } from '@/lib/inventoryListColumns';
import { useLongPress } from '@/hooks/useLongPress';
import { useDropToUploadPhotos } from '@/hooks/useDropToUploadPhotos';
import { cn } from '@/lib/utils';
import { formatGradingCompany } from '@/constants/gradingCompanies';
import { Loader2, ImagePlus, Camera } from 'lucide-react';
import { transformedImageUrl } from '@/lib/imageTransform';
import { useHasActiveEbayListing } from '@/hooks/useMyActiveEbayItems';
import { EbayListedBadge } from '@/components/inventory/EbayListedBadge';
import { QuickListPopover } from '@/components/inventory/QuickListPopover';
import { formatUsdCents } from '@/lib/formatMoney';
import { isHighValue } from './inventoryHelpers';
import { updateUserItem } from '@/queries/userItems';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface InventoryListRowProps {
  item: UserItem;
  onRowClick: (item: UserItem) => void;
  onVisibilityChange: (itemId: string, visibility: ItemVisibility) => void;
  isUpdating?: boolean;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  isOdd?: boolean;
  onToggleSelection?: (itemId: string, opts?: { shift?: boolean }) => void;
  onEnterSelectionMode?: () => void;
}

type StatusMode = 'listed' | 'offers' | 'unlisted' | 'sold';

const getStatusMode = (item: UserItem): StatusMode => {
  if (item.sold_at) return 'sold';
  if (item.visibility !== 'listed') return 'unlisted';
  if (item.is_negotiable) return 'offers';
  return 'listed';
};

export const InventoryListRow = memo(function InventoryListRow({
  item,
  onRowClick,
  onVisibilityChange,
  isUpdating = false,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
  onEnterSelectionMode,
}: InventoryListRowProps) {
  const queryClient = useQueryClient();
  const imageUrl = item.images?.[0]?.image_url || item.stock_image_url;
  const isStockImage = !item.images?.[0]?.image_url && !!item.stock_image_url;
  const categoryLabel = CATEGORY_LABELS[item.category] || item.category;
  const isOnEbay = useHasActiveEbayListing(item.id);
  const highValue = isHighValue(item);
  const isSold = !!item.sold_at;

  const longPressAtRef = useRef<number | null>(null);
  const isAfterLongPress = () => {
    if (!longPressAtRef.current) return false;
    if (Date.now() - longPressAtRef.current > 500) {
      longPressAtRef.current = null;
      return false;
    }
    return true;
  };

  const longPress = useLongPress(
    () => {
      if (isSelectionMode) return;
      longPressAtRef.current = Date.now();
      onEnterSelectionMode?.();
      onToggleSelection?.(item.id);
    },
    { disabled: isSelectionMode || !onEnterSelectionMode }
  );

  const existingPhotoCount = item.images?.length ?? 0;
  const { isDragOver, isUploading, dropHandlers } = useDropToUploadPhotos({
    itemId: item.id,
    existingCount: existingPhotoCount,
    disabled: isSelectionMode,
  });

  // ── Inline price editing ────────────────────────────────────────────
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState('');

  const startEditPrice = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPriceDraft(item.asking_price_cents ? (item.asking_price_cents / 100).toFixed(2) : '');
    setEditingPrice(true);
  }, [item.asking_price_cents]);

  const commitPrice = useCallback(async () => {
    const raw = priceDraft.trim();
    setEditingPrice(false);
    if (raw === '' && !item.asking_price_cents) return;
    const parsed = Math.round(parseFloat(raw) * 100);
    const nextCents = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (nextCents === (item.asking_price_cents ?? null)) return;
    try {
      await updateUserItem(item.id, { asking_price_cents: nextCents });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
    } catch (err) {
      console.error('inline price update failed', err);
      toast.error('Failed to update price');
    }
  }, [item.asking_price_cents, item.id, priceDraft, queryClient]);

  // ── Status cycle ────────────────────────────────────────────────────
  const status = getStatusMode(item);
  const isUnlisted = status === 'unlisted';
  const dimText = isUnlisted && !isSelectionMode;
  const cycleStatus = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (status === 'listed') {
        // Listed → Taking offers
        await updateUserItem(item.id, { is_negotiable: true });
      } else if (status === 'offers') {
        // Offers → Unlisted
        onVisibilityChange(item.id, 'private');
      } else {
        // Unlisted → Listed (only if priced; else fall through to popover trigger)
        if ((item.asking_price_cents ?? 0) > 0) {
          onVisibilityChange(item.id, 'listed');
        }
      }
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
    } catch (err) {
      console.error('status cycle failed', err);
      toast.error('Failed to update status');
    }
  }, [item.asking_price_cents, item.id, onVisibilityChange, queryClient, status]);

  // For slabs, show grading info instead of condition
  const isSlabs = item.category === 'slabs';
  const slabGradeLabel = isSlabs && item.grading_company && item.slab_grade
    ? `${formatGradingCompany(item.grading_company)} ${item.slab_grade}`
    : null;

  const getSlabGradeColor = (grade: number): string => {
    if (grade >= 9) return 'bg-success/20 text-success border-success/30';
    if (grade >= 7) return 'bg-caution/20 text-caution border-caution/30';
    if (grade >= 5) return 'bg-warning/20 text-warning border-warning/30';
    return 'bg-destructive/20 text-destructive border-destructive/30';
  };

  const conditionLabel = slabGradeLabel || CONDITION_LABELS[item.condition] || item.condition;
  const conditionColor = isSlabs && item.slab_grade
    ? getSlabGradeColor(item.slab_grade)
    : (CONDITION_COLORS[item.condition] || 'bg-muted text-muted-foreground');

  const priceDisplay = item.asking_price_cents
    ? `${formatUsdCents(item.asking_price_cents)}`
    : item.is_negotiable && item.visibility === 'listed' && !isSold
      ? 'Offers'
      : '—';
  const soldPriceDisplay = item.sold_price_cents != null ? formatUsdCents(item.sold_price_cents) : null;

  const handleClick = (e: React.MouseEvent) => {
    if (isUpdating) return;
    if (isAfterLongPress()) return;
    if (isSelectionMode) {
      onToggleSelection?.(item.id, { shift: e.shiftKey });
      return;
    }
    onRowClick(item);
  };

  return (
    <div
      data-inventory-card
      onClick={handleClick}
      {...longPress.handlers}
      {...dropHandlers}
      style={{ touchAction: 'pan-y', WebkitTouchCallout: 'none' }}
      className={cn(
        'group relative flex items-center h-[52px] border-b border-hairline cursor-pointer hover:bg-white/[0.03] transition-colors min-w-max md:min-w-0 select-none',
        (isUpdating || isUploading) && 'opacity-50 pointer-events-none',
        isSelectionMode && isSelected && 'bg-white/[0.04]',
        isDragOver && 'bg-active-gold/[0.12] ring-1 ring-inset ring-active-gold/60'
      )}
    >
      {/* Left-edge listed accent — peripheral gold bar */}
      {item.visibility === 'listed' && !isSelectionMode && !isSold && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-active-gold/80"
        />
      )}

      {(isDragOver || isUploading) && (
        <div className="absolute inset-y-0 right-3 z-20 flex items-center gap-1.5 pointer-events-none text-active-gold">
          {isUploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ImagePlus className="w-3.5 h-3.5" />
          )}
          <span className="text-[11px] font-medium">
            {isUploading ? 'Uploading…' : 'Drop to add photo'}
          </span>
        </div>
      )}

      {/* Status / Selection column */}
      <div className="w-[50px] py-2 px-2 shrink-0 flex items-center justify-center">
        {isSelectionMode ? (
          <SelectionCheckbox
            variant="box"
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelection?.(item.id, { shift: e.shiftKey });
            }}
          />
        ) : status === 'sold' ? (
          <span
            title="Sold"
            aria-label="Sold"
            className="h-3 w-3 rounded-full bg-foreground/70"
          />
        ) : status === 'unlisted' && (item.asking_price_cents ?? 0) === 0 ? (
          // Unlisted + unpriced: click opens QuickList popover
          <QuickListPopover item={item} side="right" align="center">
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              title="List for sale"
              aria-label="List for sale"
              className="h-3 w-3 rounded-full bg-white/20 hover:bg-active-gold/70 transition-colors"
            />
          </QuickListPopover>
        ) : (
          <button
            type="button"
            onClick={cycleStatus}
            title={
              status === 'listed'
                ? 'Listed — click to accept offers'
                : status === 'offers'
                  ? 'Taking offers — click to unlist'
                  : 'Unlisted — click to relist'
            }
            aria-label={`Status: ${status}. Click to cycle.`}
            className={cn(
              'h-3 w-3 rounded-full transition-colors',
              status === 'listed' && 'bg-active-gold shadow-[0_0_0_2px_hsl(var(--active-gold)/0.15)]',
              status === 'offers' && 'bg-active-gold/40 ring-1 ring-active-gold/70',
              status === 'unlisted' && 'bg-white/20 hover:bg-white/30'
            )}
          />
        )}
      </div>

      {/* Image */}
      <div className="w-[60px] p-2 shrink-0">
        {imageUrl ? (
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="relative w-10 h-10 rounded-md overflow-hidden bg-muted flex items-center justify-center cursor-zoom-in">
                <img
                  src={transformedImageUrl(imageUrl, { width: 96, height: 96, quality: 70, resize: 'cover' }) || imageUrl}
                  alt={item.title}
                  width={40}
                  height={40}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget;
                    if (img.src !== imageUrl) img.src = imageUrl;
                  }}
                />
                {item.visibility !== 'listed' && !isSold && !isSelectionMode && (
                  <div className="absolute inset-0 z-[1] bg-black/60 group-hover:bg-transparent transition-colors pointer-events-none" />
                )}
                {isStockImage && (
                  <div
                    className="absolute bottom-0 right-0 flex items-center justify-center h-3.5 w-3.5 rounded-tl-md bg-black/70 pointer-events-none"
                    title="Stock photo"
                    aria-label="Stock photo"
                  >
                    <Camera className="h-2 w-2 text-white/80" />
                  </div>
                )}
              </div>
            </HoverCardTrigger>
            <HoverCardContent side="right" align="start" className="w-auto p-1">
              <img
                src={transformedImageUrl(imageUrl, { width: 600, height: 600, quality: 80, resize: 'contain' }) || imageUrl}
                alt={item.title}
                className="w-72 h-72 object-contain rounded-md"
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.src !== imageUrl) img.src = imageUrl;
                }}
              />
            </HoverCardContent>
          </HoverCard>
        ) : (
          <ImagePlaceholder variant="compact" />
        )}
      </div>

      {/* Title */}
      <div className={cn('w-[160px] shrink-0 md:flex-1 md:basis-[260px] md:min-w-0 py-2 px-2 overflow-hidden', dimText && 'opacity-60')}>
        <div className="flex items-center gap-2 min-w-0">
          <TruncatedTooltip text={item.title} delayDuration={300} contentClassName="max-w-[300px]">
            <span className="text-sm font-medium truncate cursor-default block min-w-0">{item.title}</span>
          </TruncatedTooltip>
          {item.quantity > 1 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
              x{item.quantity}
            </Badge>
          )}
          {isSold && (
            <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px] border-white/15 bg-white/[0.04] text-muted-foreground">
              Sold
            </Badge>
          )}
          {isOnEbay && !isSold && <EbayListedBadge compact />}
        </div>
        {(item.set_name || item.card_number) && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {item.set_name}
            {item.card_number && ` #${item.card_number}`}
          </p>
        )}
      </div>

      {/* Condition */}
      <div className={cn('w-[100px] py-2 px-2 shrink-0', dimText && 'opacity-60')}>
        <Badge
          variant="outline"
          className={cn('text-[9px] lg:text-[10px] px-1.5 lg:px-2 py-0.5 font-medium border whitespace-nowrap', conditionColor)}
        >
          {conditionLabel}
        </Badge>
      </div>

      {/* Category */}
      <div className={cn('w-[100px] py-2 px-2 shrink-0', dimText && 'opacity-60')}>
        <span className="text-xs text-muted-foreground capitalize">{categoryLabel}</span>
      </div>

      {/* Price — click to edit inline */}
      <div
        className={cn('w-[100px] py-2 px-2 shrink-0 text-right', dimText && !editingPrice && 'opacity-60')}
        onClick={editingPrice ? (e) => e.stopPropagation() : undefined}
      >
        {editingPrice ? (
          <input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            name="price"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            autoFocus
            value={priceDraft}
            onChange={(e) => setPriceDraft(e.target.value.replace(/[^0-9.]/g, ''))}
            onBlur={commitPrice}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitPrice(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditingPrice(false); }
            }}
            className="w-full h-6 text-right text-xs bg-white/[0.06] border border-active-gold/40 rounded px-1.5 tabular-nums focus:outline-none focus:ring-1 focus:ring-active-gold"
            placeholder="0.00"
          />
        ) : (
          <button
            type="button"
            onClick={startEditPrice}
            title="Click to edit price"
            className={cn(
              'w-full text-right whitespace-nowrap text-xs tabular-nums rounded px-1 py-0.5 hover:bg-white/[0.05] transition-colors',
              item.asking_price_cents
                ? highValue ? 'font-semibold text-active-gold' : 'font-medium'
                : 'text-muted-foreground'
            )}
          >
            {isSold ? (soldPriceDisplay ? `Sold ${soldPriceDisplay}` : 'Sold') : priceDisplay}
          </button>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.title === nextProps.item.title &&
    prevProps.item.asking_price_cents === nextProps.item.asking_price_cents &&
    prevProps.item.is_negotiable === nextProps.item.is_negotiable &&
    prevProps.item.collectr_market_price_cents === nextProps.item.collectr_market_price_cents &&
    prevProps.item.collectr_price_override_cents === nextProps.item.collectr_price_override_cents &&
    prevProps.item.visibility === nextProps.item.visibility &&
    prevProps.item.condition === nextProps.item.condition &&
    prevProps.item.quantity === nextProps.item.quantity &&
    prevProps.item.sold_at === nextProps.item.sold_at &&
    prevProps.item.sold_price_cents === nextProps.item.sold_price_cents &&
    prevProps.item.images?.[0]?.image_url === nextProps.item.images?.[0]?.image_url &&
    prevProps.item.stock_image_url === nextProps.item.stock_image_url &&
    prevProps.isUpdating === nextProps.isUpdating &&
    prevProps.isSelectionMode === nextProps.isSelectionMode &&
    prevProps.isSelected === nextProps.isSelected
  );
});
